import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

import { AppShell } from "@/components/app/app-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { activityQuery, programsAuthedQuery } from "@/lib/queries";
import { deleteActivity, logActivity } from "@/lib/volunteer.functions";
import { reportHandledSync } from "@/lib/observability";
import { localTodayISO } from "@/lib/local-date";

export const Route = createFileRoute("/_authenticated/activity")({
  head: () => ({
    meta: [
      { title: "Activity Log | Warrior Volunteer" },
      { name: "description", content: "Activity Log in the T1D Warriors volunteer workspace." },
      { property: "og:title", content: "Activity Log | Warrior Volunteer" },
      { property: "og:description", content: "Activity Log in the T1D Warriors volunteer workspace." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ActivityPage,
});

function ActivityPage() {
  const { data: activity = [] } = useQuery(activityQuery);
  const { data: programs = [] } = useQuery(programsAuthedQuery);
  const queryClient = useQueryClient();
  const log = useServerFn(logActivity);
  const remove = useServerFn(deleteActivity);
  const [busy, setBusy] = useState(false);

  const totalHours = activity.reduce((sum, row) => sum + Number(row.hours ?? 0), 0);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    setBusy(true);
    try {
      await log({
        data: {
          program_id: String(form.get("program_id") ?? "") || null,
          activity_type: String(form.get("activity_type") ?? ""),
          hours: Number(form.get("hours") ?? 0),
          activity_date: String(form.get("activity_date") ?? ""),
          notes: String(form.get("notes") ?? ""),
        },
      });
      toast.success("Activity logged.");
      formEl.reset();
      queryClient.invalidateQueries({ queryKey: ["activity"] });
    } catch (error) {
      reportHandledSync(error, { feature: "activity", action: "handleSubmit" });
      toast.error(error instanceof Error ? error.message : "Could not log the activity.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell eyebrow="Impact" title="Activity log">
      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        <form onSubmit={handleSubmit} className="panel h-fit space-y-4 p-6">
          <h2 className="t-h3 text-ice">Log activity</h2>
          <div className="space-y-2">
            <Label htmlFor="activity_type">Activity type</Label>
            <Input id="activity_type" name="activity_type" placeholder="Event, call, prep" required />
          </div>
          <div className="space-y-2">
            <Label htmlFor="program_id">Program</Label>
            <select
              id="program_id"
              name="program_id"
              className="h-10 w-full rounded-sm border border-input bg-background px-3 text-sm text-ice"
            >
              <option value="">Unassigned</option>
              {programs.map((program) => (
                <option key={program.id} value={program.id}>
                  {program.name}
                </option>
              ))}
            </select>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="hours">Hours</Label>
              <Input id="hours" name="hours" type="number" step="0.25" min="0" defaultValue="1" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="activity_date">Date</Label>
              <Input
                id="activity_date"
                name="activity_date"
                type="date"
                required
                defaultValue={localTodayISO()}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea id="notes" name="notes" rows={3} />
          </div>
          <Button type="submit" disabled={busy} className="w-full">
            Add entry
          </Button>
        </form>

        <section>
          <div className="panel mb-4 flex items-center justify-between p-5">
            <p className="t-meta">Total hours logged</p>
            <p className="tabular-nums font-semibold text-2xl font-bold text-cyan">{totalHours}</p>
          </div>
          <div className="panel overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Program</TableHead>
                  <TableHead>Hours</TableHead>
                  <TableHead className="text-right">Remove</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activity.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-muted-foreground">
                      Nothing logged yet.
                    </TableCell>
                  </TableRow>
                ) : null}
                {activity.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="t-meta">{row.activity_date}</TableCell>
                    <TableCell>
                      <p className="text-ice">{row.activity_type}</p>
                      {row.notes ? <p className="t-meta mt-1">{row.notes}</p> : null}
                    </TableCell>
                    <TableCell>{row.programs?.name ?? "-"}</TableCell>
                    <TableCell className="tabular-nums font-semibold">{row.hours}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          if (!confirm("Delete this activity entry? This cannot be undone."))
                            return;
                          try {
                            await remove({ data: { id: row.id } });
                            toast.success("Entry deleted.");
                            queryClient.invalidateQueries({ queryKey: ["activity"] });
                          } catch (error) {
                            reportHandledSync(error, { feature: "activity", action: "handleSubmit" });
                            toast.error(
                              error instanceof Error ? error.message : "Could not delete that entry.",
                            );
                          }
                        }}

                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      </div>
    </AppShell>
  );
}
